import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import {
  getDocument,
  getDocumentDownloadUrl,
  listDocuments,
  type CanopyDocument,
  type DocumentPage,
} from '@/lib/api/endpoints/documents'

export const DOCUMENT_PAGE_SIZE = 500

export function useDocuments(companyId?: string): UseQueryResult<DocumentPage> {
  return useQuery({
    queryKey: ['documents', companyId ?? null],
    queryFn: () =>
      listDocuments({
        ...(companyId === undefined ? {} : { companyId }),
        pageSize: DOCUMENT_PAGE_SIZE,
      }),
  })
}

export function useDocumentDetail(documentId: string | null): UseQueryResult<CanopyDocument> {
  return useQuery({
    queryKey: ['documents', 'detail', documentId],
    queryFn: () => getDocument(documentId ?? ''),
    enabled: documentId !== null,
    staleTime: 30_000,
  })
}

export class StorageError extends Error {
  override readonly name = 'StorageError'
  readonly status: number | undefined

  readonly url: string

  readonly code: string | undefined

  constructor(url: string, status?: number, code?: string, options?: { cause?: unknown }) {
    super(
      status === undefined ? 'Storage unreachable' : `Storage answered ${String(status)}`,
      options,
    )
    this.status = status
    this.url = url
    this.code = code
  }
}

async function storageErrorCode(response: Response): Promise<string | undefined> {
  try {
    const body = await response.text()
    return /<Code>([^<]+)<\/Code>/.exec(body)?.[1]
  } catch {
    return undefined
  }
}

export async function downloadDocument(documentId: string, fileName: string): Promise<void> {
  const { url } = await getDocumentDownloadUrl(documentId)

  let response: Response
  try {
    response = await fetch(url)
  } catch (cause) {
    throw new StorageError(url, undefined, undefined, { cause })
  }

  if (!response.ok) {
    throw new StorageError(url, response.status, await storageErrorCode(response))
  }

  const blob = await response.blob()
  const objectUrl = URL.createObjectURL(blob)

  try {
    saveBlob(objectUrl, fileName)
  } finally {
    URL.revokeObjectURL(objectUrl)
  }
}

function saveBlob(objectUrl: string, fileName: string): void {
  const anchor = document.createElement('a')
  anchor.href = objectUrl
  anchor.download = fileName
  anchor.rel = 'noopener'
  anchor.style.display = 'none'

  document.body.append(anchor)
  anchor.click()
  anchor.remove()
}
